// src/consent/cookieConsent.js
// Single source of truth for cookie/storage consent. Setting consent here also
// applies it: Google Analytics is only loaded after an opt-in, and withdrawing
// consent stops it and removes the analytics identifiers from this browser.

// Bumped to v2 when the policy changed (Oct 2026), so everyone is asked again
const STORAGE_KEY = "cookieConsent:v2";
const LEGACY_KEYS = ["cookieConsent:v1"];
export const CONSENT_VERSION = "v2";
export const CONSENT_CHANGED_EVENT = "cookie-consent-changed";

const GA_ID = "G-HHQGPXVELY";

// First-party analytics ids, only kept on the device with analytics consent
export const ANALYTICS_STORAGE_KEYS = ["site_session_id", "site_analytics_session"];

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setConsent({ analytics }) {
  const payload = {
    necessary: true,
    analytics: Boolean(analytics),
    updatedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    LEGACY_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {}
  applyConsent();
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
  return payload;
}

export function hasConsentedTo(category) {
  if (category === "necessary") return true; // always on
  const c = getConsent();
  return Boolean(c?.[category]);
}

/**
 * Run a callback only if consent exists for a category.
 * Example: runIfConsented("analytics", initAnalytics)
 */
export function runIfConsented(category, cb) {
  if (hasConsentedTo(category)) {
    try {
      cb();
    } catch {}
  }
}

/** Opens the cookie preferences dialog from anywhere */
export function openCookieSettings() {
  window.dispatchEvent(new Event("open-cookie-settings"));
}

function loadGoogleAnalytics() {
  window[`ga-disable-${GA_ID}`] = false;
  if (typeof window.gtag === "function") return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { anonymize_ip: true });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
}

function deleteCookie(name) {
  const host = window.location.hostname;
  const domains = ["", host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
  domains.forEach((domain) => {
    document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
  });
}

function removeAnalyticsData() {
  // Stops a GA script that is already running on this page
  window[`ga-disable-${GA_ID}`] = true;
  document.cookie
    .split(";")
    .map((c) => c.split("=")[0].trim())
    .filter((name) => name === "_ga" || name.startsWith("_ga_") || name === "_gid")
    .forEach(deleteCookie);
  try {
    ANALYTICS_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {}
}

/** Applies the stored choice. Called on startup and whenever consent changes. */
export function applyConsent() {
  if (hasConsentedTo("analytics")) loadGoogleAnalytics();
  else removeAnalyticsData();
}
